import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Gauge, Wrench } from 'lucide-react';
import { netQuality } from '../lib/netCheck';
import { flyTo } from '../lib/vis';

export const openNetQ = () => window.dispatchEvent(new Event('giss-netq'));
const tone = (s) => (s >= 90 ? 'good' : s >= 60 ? 'mid' : 'bad');

const fix = (x, close) => {
  close?.();
  if (x.pt) flyTo([x.pt]);
  if (x.kind === 'open' || x.kind === 'tee') window.dispatchEvent(new CustomEvent('giss-open-end', { detail: { pipeId: x.pipeId, i: x.i } }));
  else window.dispatchEvent(new CustomEvent('giss-select', { detail: x.fitId ? { kind: 'fitting', id: x.fitId } : { kind: 'pipe', id: x.pipeId } }));
};

function Issues({ t, q, close, max = 50 }) {
  if (!q.issues.length) return <p className="nq-ok" data-testid="netq-ok">✅ {t.nqAllOk}</p>;
  return (
    <ul className="nq-list" data-testid="netq-list">
      {q.issues.slice(0, max).map((x, k) => (
        <li key={k} data-testid={`netq-issue-${k}`}>
          <span className={`nq-tag nq-${x.kind}`}>{t['nq_' + x.kind]}</span> <span className="nq-name">{x.name || ''}</span>
          <button className="btn btn-outline btn-sm" data-testid={`netq-fix-${k}`} onClick={() => fix(x, close)}><Wrench size={13} /> {t.nqFix}</button>
        </li>
      ))}
    </ul>
  );
}

const Score = ({ t, q }) => (
  <div className={`nq-score nq-${tone(q.score)}`} data-testid="netq-score"><Gauge size={18} /> {t.nqTopo || 'Topologie-Check'} <b>{q.score}</b>/100 <span>{t.nqCount.replace('{n}', q.issues.length)}</span></div>
);

// inline card in the export center (automatic check before export)
export function NetQualityCard({ t, project }) {
  const q = useMemo(() => netQuality(project), [project]);
  const [open, setOpen] = useState(false);
  if (q.empty) return null;
  return (
    <div className="nq-card" data-testid="netq-card">
      <div className="nq-head"><b>{t.nqTitle}</b><Score t={t} q={q} />{q.issues.length > 0 && <button className="btn btn-outline btn-sm" data-testid="netq-card-toggle" onClick={() => setOpen(!open)}>{open ? '▲' : t.nqShow}</button>}</div>
      {open && <Issues t={t} q={q} max={8} />}
    </div>
  );
}

// full dialog (WERKZEUGE menu)
export default function NetQualityDialog({ t, project }) {
  const [on, setOn] = useState(false);
  useEffect(() => { const f = () => setOn(true); window.addEventListener('giss-netq', f); return () => window.removeEventListener('giss-netq', f); }, []);
  const q = useMemo(() => (on ? netQuality(project) : null), [on, project]);
  if (!on || !q) return null;
  const close = () => setOn(false);
  return createPortal(
    <div className="editor-backdrop" data-testid="netq-dialog" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="editor nq-box">
        <div className="sh-head"><b>{t.nqTitle}</b><button className="icon-btn" data-testid="netq-close-btn" onClick={close}><X size={18} /></button></div>
        <Score t={t} q={q} />
        <p className="muted">{t.nqHint}</p>
        <Issues t={t} q={q} close={close} />
      </div>
    </div>,
    document.body,
  );
}
